<!-- src/views/admin/AdminEngineeringManageFormTextView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8 bg-[#F0F0F0] min-h-screen font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="max-w-7xl mx-auto pb-24 text-left">

      <!-- HEADER & ACTIONS: Midnight Premium Standard -->
      <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-12 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
        <div>
          <h1 class="text-3xl font-black text-white uppercase tracking-tight">Form Text Registry</h1>
          <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">Manage Engineering Form Text • Sentinel Safe Premium</p>
        </div>
        
        <div class="flex flex-wrap gap-3 w-full xl:w-auto items-center">
            <!-- 🛡️ NAVIGATION STANDARDISED: Uniform Return back button layout and matching vertical padding -->
            <router-link to="/admin/engineering" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
               <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
            </router-link>
        </div>
      </div>

      <!-- 🛡️ PERMISSIONS BLOCK: Guarded against unauthorised non-admin access -->
      <div v-if="!hasClearance && !loading" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
        Access Denied: Administrative clearance is required to view this registry.
      </div>

      <template v-else>
        <div v-if="loading" class="flex flex-col items-center justify-center py-32">
          <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
          <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mt-6">Decrypting Form Text...</p>
        </div>
        
        <div v-else-if="error" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
          {{ error }}
        </div>
        
        <div v-else class="space-y-10 animate-fadeIn">
          <!-- Loop through each FORM SECTION -->
          <div v-for="section in formTextSections" :key="section.id" class="bg-white p-8 rounded-[40px] border border-slate-100 shadow-2xl text-left">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-6 border-b pb-4 text-left">{{ section.title }}</h2>
            
            <div class="space-y-6 text-left">
              <h3 class="text-base font-black text-[#0A1630] uppercase tracking-wider mb-2 text-left">Introduction Text</h3>
              
              <div v-if="!editMode[section.id]" class="text-display-box p-6 bg-slate-50 rounded-2xl border border-slate-100 text-sm shadow-inner min-h-[120px] max-h-[400px] overflow-y-auto custom-scrollbar">
                <div class="custom-prose-styles" v-html="renderMarkdown(textDataMap[section.id] || 'No custom text has been set. Click Edit to add content.')"></div>
              </div>
              
              <textarea v-if="editMode[section.id]" v-model="editData[section.id]" class="form-input w-full h-96 text-xs font-mono p-5 rounded-2xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-[#20FCF1] focus:bg-white transition-all shadow-inner text-left"></textarea>
              
              <div class="mt-4 flex justify-end space-x-3">
                <button v-if="!editMode[section.id]" @click="startEditing(section.id)" class="bg-[#0A1630] text-white py-2 px-6 rounded-xl hover:bg-slate-800 transition-colors text-[10px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Edit</button>
                <button v-if="editMode[section.id]" @click="cancelEditing(section.id)" class="bg-slate-400 text-white py-2 px-6 rounded-xl hover:bg-slate-500 transition-colors text-[10px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Cancel</button>
                <button v-if="editMode[section.id]" @click="saveText(section.id)" :disabled="isSaving" class="bg-[#20FCF1] text-[#0A1630] py-2 px-6 rounded-xl hover:brightness-110 transition-colors text-[10px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm disabled:opacity-50">
                  <span v-if="isSaving">Saving...</span>
                  <span v-else>Save Text</span>
                </button>
              </div>
            </div>
          </div>

          <div class="mt-8 bg-slate-50 p-6 rounded-2xl border border-slate-100 max-w-3xl text-left">
            <p class="text-[10px] text-slate-500 font-bold uppercase tracking-wide leading-relaxed">
              You can use Markdown for formatting (e.g., `# Heading`, `* list item`, `**bold**`). Use a blank line to create a new paragraph.
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { marked } from 'marked';
import { ArrowUturnLeftIcon } from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const error = ref(null);
const textDataMap = ref({});
const editMode = ref({});
const editData = ref({});
const isSaving = ref(false);

const formTextSections = ref([
  { title: '1. Support Request Form', id: 'supportRequest' },
]);

const renderMarkdown = (text) => {
    return marked(text || '');
};

const startEditing = (id) => {
  editData.value[id] = textDataMap.value[id] || '';
  editMode.value[id] = true;
};

const cancelEditing = (id) => {
  editMode.value[id] = false;
  editData.value[id] = '';
};

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure reactive client tracking prevents race condition crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// 🛡️ SIMPLIFIED ADMINISTRATIVE CLEARANCE: Standard clearance bypasses role restrictions
const hasClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewEngineering === true ||
         userStore.permissions?.canManageEngineering === true;
});

const fetchClientText = async () => {
    const clientId = currentClientId.value;
    
    // Guard against missing clientId during initial hydration to prevent infinite spooling
    if (!clientId) {
        loading.value = true;
        setTimeout(() => {
            if (!currentClientId.value) {
                error.value = "Identity context missing. Please verify your client workspace selection.";
                loading.value = false;
            }
        }, 5000);
        return;
    }

    loading.value = true;
    error.value = null;

    try {
        const clientDocRef = doc(db, 'clients', clientId);
        const docSnap = await getDoc(clientDocRef);

        if (docSnap.exists()) {
            const clientData = docSnap.data();
            const formTexts = clientData.engineeringFormTexts || {};
            textDataMap.value = formTexts;
        } else {
            error.value = "Could not find your client configuration. Please contact support.";
        }
    } catch (err) {
        console.error("Error fetching client form text:", err);
        error.value = "Failed to load form text content.";
    } finally {
        loading.value = false;
    }
};

const saveText = async (formId) => {
    const clientId = currentClientId.value;
    if (!clientId) {
        alert("Error: Client ID not found. Cannot save.");
        return;
    }
    isSaving.value = true;
    const contentToSave = editData.value[formId] || '';
    const clientDocRef = doc(db, 'clients', clientId);
    
    const fieldPath = `engineeringFormTexts.${formId}`;

    try {
        await updateDoc(clientDocRef, { [fieldPath]: contentToSave });
        
        textDataMap.value[formId] = contentToSave;
        
        cancelEditing(formId);
        alert(`'${formTextSections.value.find(s => s.id === formId)?.title}' text saved successfully!`);
    } catch (err) {
        console.error("Error saving text:", err);
        alert(`Error saving text: ${err.message}`);
    } finally {
        isSaving.value = false;
    }
};

// 🛡️ RECONCILED SYNC: Dual trigger (watch & onMounted fallback) ensures seamless hydration for Super Admins
watch(currentClientId, (newId) => {
  if (newId) {
    fetchClientText();
  }
}, { immediate: true });

onMounted(() => {
  if (currentClientId.value) {
    fetchClientText();
  }
});
</script>

<style scoped>
.form-input {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm;
}
.text-display-box {
    min-height: 6rem;
    max-height: 20rem;
    overflow-y: auto;
}
.custom-prose-styles :deep(p) { margin-bottom: 1em; }
.custom-prose-styles :deep(h1),
.custom-prose-styles :deep(h2),
.custom-prose-styles :deep(h3) { margin-bottom: 0.5em; font-weight: 600; }
.custom-prose-styles :deep(ul),
.custom-prose-styles :deep(ol) { margin-left: 1.5rem; margin-top: 1em; margin-bottom: 1em; list-style: revert; }
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #20FCF1; }
</style>